import { Grid, useTheme, Typography, Stack, Box } from '@mui/material';
import { ReactNode, useEffect, useState } from 'react';
import { ContentTypeRichText as TContentTypeRichText, OdpHeroButtonsCollection, OdpHeroMobileBrandBannerImagesCollection, Maybe, FocalPointImage, Facility, BffFacility, SmallScreenContent, BffWorkingHours } from '@/types/generated';
import Banner from './Banner';
import LocationStatusBadge from './LocationStautsBadge';
import { checkIsFacilityOpenBff, getBackgroundImageUrl } from './ODPHero.helpers';
import { getContainerStyles, getGridStyles, getPromoTitleStyles, getHeadingStyles } from './ODPHero.styles';
import OfficeHours from './OfficeHours';
import ContentfulButton from '../../ContentfulButton';
import ContentTypeRichText from '../../ContentTypeRichText/ContentTypeRichText';
import TAGButton from '../../TAGButton';
import { resolveImagePosition } from '@shared-components/components/FocalPointImage/FocalPointImage.helpers';
import { ResponsiveImageSource } from '@shared-components/components/ImageAsset/ImageAsset';
import TAGSvgIcon from '@shared-components/components/TAGSvgIcon';
import { DAYS_OF_WEEK } from '@shared-components/constants';
import { useAppContext, useOfficeLocationContext } from '@shared-components/context';
import { useFacilityContext } from '@shared-components/context/FacilityContextProvider.ctx';
import { HeadingTag, TemplateWidth } from '@shared-components/types';
import { resolvePhoneNumberFormat, resolveContentVariable, textToJumpLink } from '@utils';
import getHeadingVariant, { getHeadingLevel } from '@utils/resolveContentfulHeadingVariant';
import { responsiveResolveTypographyVariantStyles } from '@utils/resolveTypographyVariantStyles';
export type LocationOperationStatusType = 'Open' | 'Closed' | 'Open-Planned Relo' | 'Temp-Closed' | 'Future Opening' | 'Open-Closed Weekends' | '';
export interface IODPHero {
  /**
   * An option to set the width of the ODP hero section. Can be set to Full or Inset.
   *
   * @type {TemplateWidth}
   * @memberof IODPHero
   */
  templateWidth?: TemplateWidth;
  /**
   * The background image displayed in the ODP hero section.
   * Future enhancement: move to using ImageAsset.
   * @type {Maybe<FocalPointImage>}
   * @memberof IODPHero
   */
  image: Maybe<FocalPointImage>;
  /**
   * Boolean controlled by content team to determine if the reviews should be displayed in the ODP hero section.
   *
   * @type {boolean}
   * @memberof IODPHero
   */
  showReviews?: boolean;
  /**
   * The Promo Title displayed in the ODP hero section.
   *
   * @type {string}
   * @memberof IODPHero
   */
  promoTitle?: string;
  /**
   * Boolean specifying the color of the text content. Hero text is going to be dark if the value is true.
   *
   * @type {Maybe<boolean>}
   * @memberof IHero
   */
  darkTextColor?: Maybe<boolean>;
  /**
   * The title displayed in the ODP hero section.
   *
   * @type {string}
   * @memberof IODPHero
   */
  title?: string;
  /**
   * The title heading tag. Can be h1, h2, h3, or h4
   *
   * @type {HeadingTag}
   * @memberof IODPHero
   */
  titleHtag?: HeadingTag;
  /**
   * bodyCopy alias of description displayed below the title in the ODP hero section .
   *
   * @type {HeadingTag}
   * @memberof IODPHero
   */
  bodyCopy: Maybe<TContentTypeRichText>;
  /**
   * A boolean set in contentful to determine if the address should be displayed in the ODP hero section.
   *
   * @type {HeadingTag}
   * @memberof IODPHero
   */
  showAddress?: boolean;
  /**
   * A boolean set in contentful to determine if the phone should be displayed in the ODP hero section.
   *
   * @type {HeadingTag}
   * @memberof IODPHero
   */
  showPhoneNumber?: boolean;
  /**
   * A boolean set in contentful to determine if the hours should be displayed in the ODP hero section.
   *
   * @type {HeadingTag}
   * @memberof IODPHero
   */
  showHours?: boolean;
  showFax?: boolean;
  showEmail?: boolean;
  /**
   * ODP Hero cta buttons
   *
   * @type {Maybe<OdpHeroButtonsCollection>}
   * @memberof IODPHero
   */
  buttonsCollection?: Maybe<OdpHeroButtonsCollection>;
  /**
   * ODP Hero cta buttons
   *
   * @type {string}
   * @memberof IODPHero
   */
  googleMapsBusinessQuery?: string;
  /**
   * ODP Hero Appointment Data Source
   * InHouse, RevSpring, None
   *
   * @type {string}
   * @memberof IODPHero
   */
  appointmentDataSource?: string;
  /**
   * A boolean set in contentful to determine if the Location Status Badge should be displayed in the ODP hero.
   *
   * @type {boolean}
   * @memberof IODPHero
   */
  showLocationStatusBadge?: boolean;
  /**
   * The label displayed in the ODP hero office hours.
   *
   * @type {string}
   * @memberof IODPHero
   */
  officeHoursLabel?: string;
  /**
   * The label displayed in the ODP hero call center hours.
   *
   * @type {string}
   * @memberof IODPHero
   */
  callCenterLabel?: string;
  /**
   * The secondary image displayed in the ODP hero section if provided.
   *
   * @type {Maybe<FocalPointImage>}
   * @memberof IODPHero
   */
  secondaryImage?: Maybe<FocalPointImage>;
  /**
   * The collection of mobile brand banner images displayed above the ODP hero section.
   *
   * @type {Maybe<OdpHeroMobileBrandBannerImagesCollection>}
   * @memberof IODPHero
   */
  mobileBrandBannerImagesCollection?: Maybe<OdpHeroMobileBrandBannerImagesCollection>;
  /**
   * Used to identify a component on a page for the purpose of jumping to it using the anchor link.
   *
   * @type {Maybe<string>}
   * @memberof IODPHero
   */
  anchorId?: Maybe<string>;
  /**
   * Contains small screen override for the background image.
   *
   * @type {Maybe<SmallScreenContent>}
   * @memberof IODPHero
   */
  contentSmallScreen?: Maybe<SmallScreenContent>;
}
export default function OdpHero({
  title,
  titleHtag,
  promoTitle,
  image,
  bodyCopy: description,
  showAddress = true,
  showPhoneNumber = true,
  showReviews = true,
  showHours = true,
  showFax = false,
  showEmail = false,
  buttonsCollection,
  templateWidth = 'Full',
  googleMapsBusinessQuery,
  showLocationStatusBadge = false,
  darkTextColor = false,
  officeHoursLabel,
  callCenterLabel,
  secondaryImage,
  mobileBrandBannerImagesCollection,
  anchorId,
  contentSmallScreen
}: Readonly<IODPHero>) {
  const [state, setState] = useState({
    showHours: false,
    isBadgeReady: false,
    workingHoursForWeek: [] as BffWorkingHours[],
    locationStatus: 'Closed' as LocationOperationStatusType
  });
  const theme = useTheme();
  const {
    officeInfo
  } = useFacilityContext(true);
  const {
    officeInfo: legalCenterName
  } = useOfficeLocationContext();
  const {
    config
  } = useAppContext();
  const titleTag = getHeadingVariant(titleHtag as HeadingTag);
  const street = `${(officeInfo ?? legalCenterName)?.address?.address1 ?? ''}`;
  const city = `${(officeInfo ?? legalCenterName)?.address?.city ?? ''}`;
  const officeState = `${(officeInfo ?? legalCenterName)?.address?.stateCode ?? ''}`;
  const zip = `${(officeInfo ?? legalCenterName)?.address?.zipCode ?? ''}`;
  const address = `${street} ${city}, ${officeState} ${zip}`;
  const phoneNumber = resolvePhoneNumberFormat(`${officeInfo?.technicalInfrastructure?.phoneNumber ?? legalCenterName.phoneNumber ?? ''}`);
  const faxNumber = resolvePhoneNumberFormat(officeInfo?.technicalInfrastructure?.faxNumber ?? legalCenterName.faxNumber ?? '');
  const email = officeInfo?.technicalInfrastructure?.email ?? legalCenterName.email;
  const workingHours = officeInfo?.workingHours || legalCenterName?.workingHours;
  const textColor = darkTextColor ? 'text.secondary' : 'text.light';
  const useFacilityBeffeDataSource = config.featureFlags.useFacilityBeffeDataSource;
  const handleHoursClick = () => {
    setState({
      ...state,
      showHours: true
    });
  };
  const handleClose = () => {
    setState({
      ...state,
      showHours: false
    });
  };
  const getGoogleLink = () => {
    return resolveContentVariable(`${googleMapsBusinessQuery || config.googleMapBusinessQuery}`, officeInfo as Maybe<Facility>);
  };
  const desktopImageFocalPosition = resolveImagePosition(image?.focalPoint, image?.image);
  const mobileImageFocalPosition = contentSmallScreen?.focalPointImageSmallScreen ? resolveImagePosition(contentSmallScreen?.focalPointImageSmallScreen?.focalPoint, contentSmallScreen?.focalPointImageSmallScreen?.image) : null;
  const imgUrl = secondaryImage?.image?.url ? getBackgroundImageUrl(image?.image?.url ?? '', secondaryImage.image.url ?? '', legalCenterName.originalBrand) : image?.image?.url ?? '';
  const mobileImgUrl = contentSmallScreen?.focalPointImageSmallScreen?.image?.url ?? null;
  const isFacilityOpenBff = state.locationStatus === 'Temp-Closed' as LocationOperationStatusType || state.locationStatus === 'Future Opening' as LocationOperationStatusType ? 'Not in operation' : checkIsFacilityOpenBff(state.workingHoursForWeek);
  const resolvedTitleComponent = titleHtag === 'normal' ? 'p' : titleHtag ?? 'h1';
  let locationStatusBadgeContent: ReactNode = <Grid item height={'1.25rem'} />;
  if (showLocationStatusBadge && state.isBadgeReady) {
    locationStatusBadgeContent = <Grid item data-testid="odp_hero_location_status_badge">
				<LocationStatusBadge fallBackComponent={<Box sx={{
        height: '1.25rem'
      }} />} isFacilityOpen={isFacilityOpenBff} locationStatus={state.locationStatus} />
			</Grid>;
  } else if (!showLocationStatusBadge) {
    locationStatusBadgeContent = null;
  }
  useEffect(() => {
    setState({
      ...state,
      isBadgeReady: isFacilityOpenBff !== null && isFacilityOpenBff !== undefined
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFacilityOpenBff]);
  useEffect(() => {
    if (workingHours) {
      const workingHoursForWeek = workingHours?.filter(day => DAYS_OF_WEEK.includes(day?.date as string));
      const officeInformation = officeInfo as BffFacility;
      setState({
        ...state,
        ...(workingHoursForWeek?.length ? {
          workingHoursForWeek: workingHoursForWeek as BffWorkingHours[]
        } : {}),
        ...(officeInformation && 'status' in officeInformation ? {
          locationStatus: officeInformation.status as LocationOperationStatusType || ''
        } : {})
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingHours]);
  return <>
			{mobileBrandBannerImagesCollection?.items?.length ? <Banner bannerImages={mobileBrandBannerImagesCollection?.items?.filter((image): image is FocalPointImage => image !== null)} originalBrand={legalCenterName.originalBrand ?? ''} /> : null}

			{state.showHours && workingHours && <OfficeHours handleClose={handleClose} workingHours={workingHours} useFacilityBeffeDataSource={useFacilityBeffeDataSource} officeHoursLabel={officeHoursLabel} callCenterLabel={callCenterLabel} />}

			<Grid {...anchorId && {
      id: textToJumpLink(anchorId)
    }} container flexWrap={'wrap'} data-testid="odp_hero_container" sx={getContainerStyles(theme, imgUrl ?? '', templateWidth, desktopImageFocalPosition, mobileImageFocalPosition, mobileImgUrl)} data-sentry-element="Grid" data-sentry-source-file="ODPHero.tsx">
				<ResponsiveImageSource defaultSrc={imgUrl ?? ''} mobileSrc={mobileImgUrl} includeSourceElement={false} data-sentry-element="ResponsiveImageSource" data-sentry-source-file="ODPHero.tsx" />
				<Grid item container direction="column" data-testid="odp_hero_grid" sx={getGridStyles(theme)} xs={12} md={6} gap={2} data-sentry-element="Grid" data-sentry-source-file="ODPHero.tsx">
					{/* Location Status Badge */}
					{locationStatusBadgeContent}
					{/* Promo title */}

					{promoTitle && <Grid item data-testid="odp_hero_promo_title">
							<Typography data-testid="odp_hero_promo_title_text" variant={'heroLarge'} color="text.light" sx={[...responsiveResolveTypographyVariantStyles(theme, 'heroLarge', 'heroXL'), getPromoTitleStyles]}>
								{promoTitle}
							</Typography>
						</Grid>}

					{/* Title */}
					<Grid item data-testid="odp_hero_item_title" data-sentry-element="Grid" data-sentry-source-file="ODPHero.tsx">
						<Typography data-testid="odp_hero_item_title_text" variant={titleTag} sx={getHeadingStyles(theme, darkTextColor as boolean)} component={resolvedTitleComponent} role="heading" aria-level={getHeadingLevel(titleHtag as HeadingTag) || 1} data-sentry-element="Typography" data-sentry-source-file="ODPHero.tsx">
							{resolveContentVariable(title as string, officeInfo as Maybe<Facility>)}
						</Typography>
					</Grid>

					{/* Description */}
					{description?.content && <Grid item data-testid="odp_hero_item_description" pb={1.5}>
							<ContentTypeRichText {...description} disableGutters disablePaddingOn={['Top', 'Bottom']} contentMaxWidth="100%" backgroundColor="transparent" docProps={{
            color: `${textColor}`,
            variant: 'bodyLargeBook',
            textAlign: {
              xs: 'center',
              md: 'left'
            }
          }} />
						</Grid>}

					{/* Office Info */}
					<Grid item data-testid="odp_hero_item_office_block" data-sentry-element="Grid" data-sentry-source-file="ODPHero.tsx">
						<Grid container spacing={2.5} justifyContent={{
            xs: 'center',
            md: 'flex-start'
          }} alignItems={{
            xs: 'center',
            md: 'flex-start'
          }} data-sentry-element="Grid" data-sentry-source-file="ODPHero.tsx">
							{/* Address link */}
							{showAddress && street && <Grid item data-testid="odp_hero_item_address">
									<TAGButton size="S" startIcon={<TAGSvgIcon icon="MapMarker" size={24} />} variant="tertiaryHC" href={getGoogleLink()} target="_blank">
										{address}
									</TAGButton>
								</Grid>}

							{/* Phone number */}
							{showPhoneNumber && phoneNumber && <Grid item data-testid="odp_hero_item_phone">
									<TAGButton size="S" startIcon={<TAGSvgIcon icon="CallIcon" size={24} />} variant="tertiaryHC" href={`tel:${phoneNumber}`}>
										{phoneNumber}
									</TAGButton>
								</Grid>}

							{/* Fax */}
							{showFax && faxNumber && <Grid item data-testid="odp_hero_item_fax_number">
									<Stack alignItems="center" direction="row" gap={1} p=".3rem">
										<TAGSvgIcon icon="Fax" htmlColor="white" size={24} />
										<Typography color="text.light" title="Fax Number">
											{faxNumber}
										</Typography>
									</Stack>
								</Grid>}

							{/* Email */}
							{showEmail && email && <Grid item data-testid="odp_hero_item_email">
									<TAGButton size="S" startIcon={<TAGSvgIcon icon="Email" size={24} />} variant="tertiaryHC" href={`mailto:${email}?subject=Contact from Office Page`} target="_blank">
										Email
									</TAGButton>
								</Grid>}

							{/* Hours */}
							{showHours && workingHours?.length ? <Grid item data-testid="odp_hero_item_hours">
									<TAGButton dataTestId="odp_hero_item_hours_button" size="S" startIcon={<TAGSvgIcon icon="Clock" size={24} />} variant="tertiaryHC" onClick={handleHoursClick}>
										Hours
									</TAGButton>
								</Grid> : null}
						</Grid>
					</Grid>

					{/* Schedule CTA */}
					{buttonsCollection?.items?.map(button => {
          return button ? <Grid key={button?.sys?.id}>
								<ContentfulButton {...button} dataTestId="odp_hero_item_button" />
							</Grid> : null;
        })}
				</Grid>
			</Grid>
		</>;
}